import { useState } from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { Link } from 'react-router-dom'
import { Button, Card, CardContent, CardHeader, CardTitle, Input } from '@agms/ui'
import { usePermissions } from '../hooks/useAuth'
import api from '../lib/api'
import { apiErrorMessage, apiErrorStatus, apiValidationErrors } from '../hooks/useErrorHandler'
import { formatStage } from '../lib/format'
import { GoogleCalendarConnectionCard } from '../components/GoogleCalendarConnectionCard'
import { CardTitle as SectionCardTitle, PageHeader, SurfaceCard } from '../components/ui'

interface SalesRep {
  id: string
  name: string
  sales_rep_id: string
  email: string | null
  is_active: boolean
  user_id: string | null
  user?: { id: string; name: string } | null
}

interface LinkableSalesRepUser {
  id: string
  name: string
  is_active: boolean
}

interface FiservMidAllocation {
  mid: string
  status: 'reserved' | 'consumed' | 'unassigned'
  application_id: string | null
  lead_id: string | null
  dba_name: string | null
  legal_name: string | null
  contact_name: string | null
  lead_stage: string | null
  fiserv_ext_ref_id: string | null
  fiserv_as_ref_id: string | null
  fiserv_submitted_at: string | null
  is_submitted: boolean
  allocated_at: string
}

interface FiservMidPoolResponse {
  pool: {
    name: string
    start_mid: string
    end_mid: string
    next_mid: string
  } | null
  summary: {
    total: number
    available: number
    allocated: number
    reserved: number
    consumed: number
    unassigned: number
  }
  allocations: FiservMidAllocation[]
}

function useSalesReps(enabled: boolean) {
  return useQuery({
    queryKey: ['settings-sales-reps'],
    enabled,
    queryFn: async () => {
      const resp = await api.get<SalesRep[]>('/settings/sales-reps')
      return resp.data
    },
  })
}

function useLinkableSalesRepUsers(enabled: boolean) {
  return useQuery({
    queryKey: ['settings-linkable-sales-reps'],
    enabled,
    queryFn: async () => {
      const resp = await api.get<LinkableSalesRepUser[]>('/settings/sales-reps/linkable-users')
      return resp.data.map(({ id, name, is_active }) => ({ id, name, is_active }))
    },
  })
}

function useFiservMidPool(search: string, status: string) {
  return useQuery({
    queryKey: ['settings-fiserv-mid-pool', search, status],
    queryFn: async () => {
      const resp = await api.get<FiservMidPoolResponse>('/settings/fiserv-mid-pool', {
        params: {
          search: search || undefined,
          status: status || undefined,
        },
      })
      return resp.data
    },
  })
}

export function SettingsPage() {
  const queryClient = useQueryClient()
  const { canManageUsers, canManagePricingTemplates, canManageScheduleA, canManageTicketTypes, canManageHelpHub, hasRole } = usePermissions()
  const canLinkSalesRep = hasRole(['management', 'super_admin'])
  const { data: reps, isLoading } = useSalesReps(canLinkSalesRep)
  const { data: linkableUsers } = useLinkableSalesRepUsers(canLinkSalesRep)
  const [midSearch, setMidSearch] = useState('')
  const [midStatus, setMidStatus] = useState('')
  const { data: midPool, isLoading: midPoolLoading, error: midPoolError } = useFiservMidPool(midSearch, midStatus)
  const [showForm, setShowForm] = useState(false)
  const [editingId, setEditingId] = useState<string | null>(null)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const handleSave = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault()
    setError(null)
    setSaving(true)

    const form = new FormData(e.currentTarget)
    const data: {
      name: string
      sales_rep_id: string
      email: string | null
      is_active: boolean
      user_id?: string | null
    } = {
      name: form.get('name') as string,
      sales_rep_id: form.get('sales_rep_id') as string,
      email: (form.get('email') as string) || null,
      is_active: true,
    }
    if (canLinkSalesRep) {
      data.user_id = (form.get('user_id') as string) || null
    }

    try {
      if (editingId) {
        await api.put(`/settings/sales-reps/${editingId}`, data)
      } else {
        await api.post('/settings/sales-reps', data)
      }
      await queryClient.invalidateQueries({ queryKey: ['settings-sales-reps'] })
      await queryClient.invalidateQueries({ queryKey: ['settings-linkable-sales-reps'] })
      // Also refresh the TurboApp dropdown cache
      await queryClient.invalidateQueries({ queryKey: ['salesReps'] })
      setShowForm(false)
      setEditingId(null)
    } catch (err) {
      const fieldErrors = apiValidationErrors(err)
      const msg = fieldErrors?.user_id?.[0]
        ?? fieldErrors?.north_rep_code?.[0]
        ?? fieldErrors?.sales_rep_id?.[0]
        ?? apiErrorMessage(err, 'Failed to save')
      setError(msg)
    } finally {
      setSaving(false)
    }
  }

  const handleDeactivate = async (id: string) => {
    if (!confirm('Deactivate this sales rep? They will no longer appear in the TurboApp dropdown.')) return
    await api.delete(`/settings/sales-reps/${id}`)
    await queryClient.invalidateQueries({ queryKey: ['settings-sales-reps'] })
    await queryClient.invalidateQueries({ queryKey: ['salesReps'] })
  }

  const startEdit = (rep: SalesRep) => {
    setEditingId(rep.id)
    setShowForm(true)
    setError(null)
  }

  const editingRep = reps?.find((r) => r.id === editingId)
  const linkChoices = (() => {
    const choices = linkableUsers ?? []
    const current = editingRep?.user
    if (!current || choices.some((user) => user.id === current.id)) {
      return choices
    }
    return [...choices, { id: current.id, name: current.name, is_active: true }]
  })()
  const formatNumber = (value: number | undefined) => (value ?? 0).toLocaleString()
  const formatDateTime = (value: string | null) => {
    if (!value) return '—'
    return new Date(value).toLocaleString()
  }
  const statusBadgeClass = (status: FiservMidAllocation['status']) => {
    if (status === 'consumed') return 'bg-emerald-50 text-emerald-700'
    if (status === 'unassigned') return 'bg-gray-100 text-gray-600'
    return 'bg-blue-50 text-blue-700'
  }

  return (
    <div className="space-y-6">
      <PageHeader title="Settings" subtitle="System configuration and reference data" />

      <GoogleCalendarConnectionCard />

      {canManageUsers() && (
        <SurfaceCard>
            <div className="flex items-center justify-between">
              <div>
                <SectionCardTitle>User Management</SectionCardTitle>
                <p className="mt-1 text-xs text-gray-500">
                  Create users, assign roles, disable access, resend invites, and reset 2FA.
                </p>
              </div>
              <Link to="/settings/users">
                <Button>Manage Users</Button>
              </Link>
            </div>
        </SurfaceCard>
      )}

      {canManagePricingTemplates() && (
        <SurfaceCard>
            <div className="flex items-center justify-between">
              <div>
                <SectionCardTitle>Pricing Templates</SectionCardTitle>
                <p className="mt-1 text-xs text-gray-500">
                  Manage standard rate structures, archive retired templates, and choose the default for new applications.
                </p>
              </div>
              <Link to="/settings/pricing-templates">
                <Button>Manage Templates</Button>
              </Link>
            </div>
        </SurfaceCard>
      )}

      {canManageScheduleA() && (
        <SurfaceCard>
            <div className="flex items-center justify-between">
              <div>
                <SectionCardTitle>Schedule A</SectionCardTitle>
                <p className="mt-1 text-xs text-gray-500">
                  Create and revise the buy-rate schedule used by statement analysis. Other roles can select a version but cannot change it.
                </p>
              </div>
              <Link to="/settings/schedule-a">
                <Button>Manage Schedule A</Button>
              </Link>
            </div>
        </SurfaceCard>
      )}

      {canManageScheduleA() && (
        <SurfaceCard>
            <div className="flex items-center justify-between">
              <div>
                <SectionCardTitle>Interchange document review</SectionCardTitle>
                <p className="mt-1 text-xs text-gray-500">
                  Review public Visa and Mastercard document changes. Acknowledging a file does not replace stored rates.
                </p>
              </div>
              <Link to="/settings/interchange-reviews">
                <Button>Review documents</Button>
              </Link>
            </div>
        </SurfaceCard>
      )}

      {canManageTicketTypes() && (
        <SurfaceCard>
            <div className="flex items-center justify-between">
              <div>
                <SectionCardTitle>Ticket Types</SectionCardTitle>
                <p className="mt-1 text-xs text-gray-500">
                  Manage reusable ticket templates, defaults, role visibility, and internal checklists.
                </p>
              </div>
              <Link to="/settings/ticket-types">
                <Button>Manage Ticket Types</Button>
              </Link>
            </div>
        </SurfaceCard>
      )}

      {canManageHelpHub() && (
        <SurfaceCard>
            <div className="flex items-center justify-between">
              <div>
                <SectionCardTitle>Help Hub</SectionCardTitle>
                <p className="mt-1 text-xs text-gray-500">
                  Manage internal knowledge base categories and Help Hub organization.
                </p>
              </div>
              <Link to="/help/categories">
                <Button>Manage Categories</Button>
              </Link>
            </div>
        </SurfaceCard>
      )}

      {/* Sales Reps. The list includes the linked user, so only roles that manage reps load it. */}
      {canLinkSalesRep && <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle>Sales Representatives</CardTitle>
            <Button onClick={() => { setEditingId(null); setShowForm(true); setError(null) }}>
              Add Rep
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          <p className="mb-4 text-sm text-gray-600">
            Sales responsibility codes used on Fiserv applications.
          </p>

          {showForm && (
            <div style={{
              padding: '16px',
              backgroundColor: '#f9fafb',
              borderRadius: '8px',
              marginBottom: '16px',
              border: '1px solid #e5e7eb',
            }}>
              <form key={editingId ?? 'new'} onSubmit={handleSave} className="space-y-3">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                  <Input
                    name="name"
                    label="Full Name"
                    required
                    defaultValue={editingRep?.name ?? ''}
                    placeholder="Jane Smith"
                  />
                  <Input
                    name="sales_rep_id"
                    label="Sales Responsibility Code"
                    required
                    defaultValue={editingRep?.sales_rep_id ?? ''}
                    placeholder="2508"
                    maxLength={4}
                    pattern="25[0-9]{2}"
                  />
                  <Input
                    name="email"
                    label="Email (optional)"
                    type="email"
                    defaultValue={editingRep?.email ?? ''}
                    placeholder="jane@agms.com"
                  />
                  {canLinkSalesRep && (
                    <label className="block text-sm" htmlFor="sales-rep-user-id">
                      <span className="mb-1 block text-xs font-medium text-gray-500">CRM user</span>
                      <select
                        id="sales-rep-user-id"
                        name="user_id"
                        defaultValue={editingRep?.user_id ?? ''}
                        aria-describedby="sales-rep-user-help"
                        className="w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-sm"
                      >
                        <option value="">Not linked</option>
                        {linkChoices.map((user) => (
                          <option key={user.id} value={user.id}>
                            {user.name}{user.is_active ? '' : ' (inactive)'}
                          </option>
                        ))}
                      </select>
                      <span id="sales-rep-user-help" className="mt-1 block text-xs text-gray-500">
                        Linking a code lets that sales rep open merchants that use it. The configured house code does not grant access.
                      </span>
                    </label>
                  )}
                </div>
                {error && (
                  <p style={{ color: '#dc2626', fontSize: '13px' }}>{error}</p>
                )}
                <div className="flex gap-2 justify-end">
                  <Button type="button" variant="secondary" onClick={() => { setShowForm(false); setEditingId(null) }}>
                    Cancel
                  </Button>
                  <Button type="submit" isLoading={saving}>
                    {editingId ? 'Update' : 'Add Sales Rep'}
                  </Button>
                </div>
              </form>
            </div>
          )}

          {isLoading && <p className="text-gray-500">Loading...</p>}

          {reps && reps.length > 0 && (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200 text-sm">
                <thead>
                  <tr className="bg-gray-50">
                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500">Name</th>
                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500">Responsibility Code</th>
                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500">Email</th>
                    {canLinkSalesRep && (
                      <th className="px-4 py-2 text-left text-xs font-medium text-gray-500">CRM user</th>
                    )}
                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500">Status</th>
                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {reps.map((rep) => (
                    <tr key={rep.id}>
                      <td className="px-4 py-2 text-gray-900 font-medium">{rep.name}</td>
                      <td className="px-4 py-2 text-gray-700 font-mono">{rep.sales_rep_id}</td>
                      <td className="px-4 py-2 text-gray-500">{rep.email ?? '—'}</td>
                      {canLinkSalesRep && (
                        <td className="px-4 py-2 text-gray-700">{rep.user?.name ?? 'Not linked'}</td>
                      )}
                      <td className="px-4 py-2">
                        <span style={{
                          padding: '2px 8px',
                          borderRadius: '9999px',
                          fontSize: '11px',
                          fontWeight: 600,
                          backgroundColor: rep.is_active ? '#dcfce7' : '#f3f4f6',
                          color: rep.is_active ? '#166534' : '#6b7280',
                        }}>
                          {rep.is_active ? 'Active' : 'Inactive'}
                        </span>
                      </td>
                      <td className="px-4 py-2 space-x-2">
                        <button
                          onClick={() => startEdit(rep)}
                          style={{ fontSize: '13px', color: '#3b82f6', cursor: 'pointer', background: 'none', border: 'none' }}
                        >
                          Edit
                        </button>
                        {rep.is_active && (
                          <button
                            onClick={() => handleDeactivate(rep.id)}
                            style={{ fontSize: '13px', color: '#dc2626', cursor: 'pointer', background: 'none', border: 'none' }}
                          >
                            Deactivate
                          </button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {reps && reps.length === 0 && !showForm && (
            <p className="text-gray-500 text-center py-4">No sales reps configured. Click "Add Rep" to get started.</p>
          )}
        </CardContent>
      </Card>}

      {/* Fiserv MID Pool */}
      <Card>
        <CardHeader>
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div>
              <CardTitle>Fiserv MID Pool</CardTitle>
              <p className="mt-1 text-xs text-gray-500">
                Pre-assigned Omaha merchant numbers reserved automatically during SubmitMPA.
              </p>
            </div>
            {midPool?.pool && (
              <div className="text-right text-xs text-gray-500">
                <div className="font-mono text-gray-700">
                  {midPool.pool.start_mid} - {midPool.pool.end_mid}
                </div>
                <div>Next available: <span className="font-mono">{midPool.pool.next_mid}</span></div>
              </div>
            )}
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          {midPoolError && (
            <p className="rounded-md border border-red-100 bg-red-50 p-3 text-sm text-red-700">
              {apiErrorStatus(midPoolError) === 403
                ? 'MID pool is only visible to management and super admin users.'
                : 'Could not load the MID pool.'}
            </p>
          )}

          {!midPoolError && (
            <>
              <div className="grid grid-cols-2 gap-3 md:grid-cols-5">
                {[
                  ['Available', midPool?.summary.available],
                  ['Reserved', midPool?.summary.reserved],
                  ['Consumed', midPool?.summary.consumed],
                  ['Allocated', midPool?.summary.allocated],
                  ['Total Pool', midPool?.summary.total],
                ].map(([label, value]) => (
                  <div key={label} className="rounded-md border border-slate-200 bg-slate-50 p-3">
                    <div className="text-lg font-semibold text-gray-900">
                      {midPoolLoading ? '...' : formatNumber(value as number)}
                    </div>
                    <div className="text-xs text-gray-500">{label}</div>
                  </div>
                ))}
              </div>

              <div className="flex flex-wrap items-center gap-2 rounded-md border border-slate-200 bg-slate-50/80 p-3">
                <div className="min-w-64 flex-1">
                  <Input
                    type="search"
                    aria-label="Search MID pool"
                    placeholder="Search MID, DBA, Ext Ref, AS Ref..."
                    value={midSearch}
                    onChange={(e) => setMidSearch(e.target.value)}
                  />
                </div>
                <select
                  aria-label="MID pool status filter"
                  value={midStatus}
                  onChange={(e) => setMidStatus(e.target.value)}
                  className="rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
                >
                  <option value="">All statuses</option>
                  <option value="reserved">Reserved</option>
                  <option value="consumed">Consumed</option>
                  <option value="unassigned">Unassigned</option>
                </select>
              </div>

              {midPoolLoading && <p className="text-sm text-gray-500">Loading MID pool...</p>}

              {!midPoolLoading && midPool && midPool.allocations.length === 0 && (
                <p className="rounded-md border border-dashed border-slate-300 bg-white py-6 text-center text-sm text-gray-500">
                  No MID allocations match the current filters.
                </p>
              )}

              {!midPoolLoading && midPool && midPool.allocations.length > 0 && (
                <div className="overflow-x-auto rounded-md border border-slate-200">
                  <table className="min-w-full divide-y divide-gray-200 text-sm">
                    <thead className="bg-slate-100">
                      <tr>
                        <th className="px-4 py-2 text-left text-xs font-medium uppercase text-gray-500">MID</th>
                        <th className="px-4 py-2 text-left text-xs font-medium uppercase text-gray-500">Status</th>
                        <th className="px-4 py-2 text-left text-xs font-medium uppercase text-gray-500">Account</th>
                        <th className="px-4 py-2 text-left text-xs font-medium uppercase text-gray-500">Ext Ref</th>
                        <th className="px-4 py-2 text-left text-xs font-medium uppercase text-gray-500">AS Ref</th>
                        <th className="px-4 py-2 text-left text-xs font-medium uppercase text-gray-500">Allocated</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100 bg-white">
                      {midPool.allocations.map((allocation) => (
                        <tr key={allocation.mid} className="hover:bg-slate-50">
                          <td className="px-4 py-2 font-mono text-gray-900">{allocation.mid}</td>
                          <td className="px-4 py-2">
                            <span className={`inline-flex rounded-full px-2 py-0.5 text-xs font-semibold capitalize ${statusBadgeClass(allocation.status)}`}>
                              {allocation.status}
                            </span>
                          </td>
                          <td className="px-4 py-2">
                            <div className="font-medium text-gray-900">
                              {allocation.dba_name ?? allocation.legal_name ?? '—'}
                            </div>
                            <div className="text-xs text-gray-500">
                              {allocation.contact_name ?? formatStage(allocation.lead_stage)}
                            </div>
                          </td>
                          <td className="px-4 py-2 font-mono text-xs text-gray-600">
                            {allocation.fiserv_ext_ref_id ?? '—'}
                          </td>
                          <td className="px-4 py-2 font-mono text-xs text-gray-600">
                            {allocation.fiserv_as_ref_id ?? '—'}
                          </td>
                          <td className="px-4 py-2 text-xs text-gray-500">
                            {formatDateTime(allocation.allocated_at)}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </>
          )}
        </CardContent>
      </Card>
    </div>
  )
}
